<template>
    <div class="h-full overflow-y-auto min-w-0">
        <div class="grid grid-cols-1 gap-px bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden min-w-0">
            <!-- Day header -->
            <div class="bg-white dark:bg-gray-800 p-4 text-center">
                <div class="text-lg font-semibold text-gray-900 dark:text-white">
                    {{ formatFullDate(currentDate) }}
                </div>
            </div>
            
            <!-- Time slots -->
            <div 
                v-for="hour in 24" 
                :key="hour"
                class="bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 relative h-16 min-w-0 overflow-hidden"
                @click="addEventAtTime(currentDate, hour - 1)"
            >
                <div class="flex min-w-0 h-full">
                    <div class="w-20 text-xs text-gray-500 dark:text-gray-400 p-2 text-right border-r border-gray-100 dark:border-gray-700 flex-shrink-0">
                        {{ formatHour(hour - 1) }}
                    </div>
                    <div class="flex-1 relative min-w-0 overflow-hidden py-1">
                        <!-- Events in this time slot -->
                        <div 
                            v-for="event in getEventsForDateAndTime(currentDate, hour - 1).slice(0, 2)" 
                            :key="event.id"
                            @click.stop="selectEvent(event)"
                            class="absolute left-0 right-0 mx-1 text-xs p-1 rounded cursor-pointer truncate"
                            :class="getEventClasses(event)"
                            :style="{ ...getEventStyles(event), top: `${(event.startTime.getMinutes() / 60) * 100}%` }"
                            :title="event.title"
                        >
                            {{ event.title }}
                        </div>
                        <!-- See more button when there are more than 3 events in this time slot -->
                        <button 
                            v-if="getEventsForDateAndTime(currentDate, hour - 1).length > 2"
                            @click.stop="seeMoreEvents(currentDate, hour - 1)"
                            class="absolute left-0 right-0 mx-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors border border-indigo-200 dark:border-indigo-700 hover:border-indigo-300 dark:hover:border-indigo-600"
                            :style="{ top: `${(3 * 20) + 5}px` }"
                        >
                            +{{ getEventsForDateAndTime(currentDate, hour - 1).length - 3 }} more
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue'
import { isTailwindColor } from '@/utilities/color'
import { useI18n } from 'vue-i18n'

// Props
const props = defineProps({
    currentDate: {
        type: Date,
        default: () => new Date()
    },
    events: {
        type: Array,
        default: () => []
    },
    categories: {
        type: Array,
        default: () => []
    }
})

// Emits
const emit = defineEmits(['event-select', 'add-event', 'see-more'])

const { locale } = useI18n()

// Methods
const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime();
}

const getEventsForDateAndTime = (date, hour) => {
    const slotStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, 0, 0, 0)
    const slotEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, 59, 59, 999)
    return props.events.filter(event => {
        const start = new Date(event.startTime || event.date)
        const end = event.endTime ? new Date(event.endTime) : start
        return start.getTime() <= slotEnd.getTime() && end.getTime() > slotStart.getTime()
    })
}

const getEventStyles = (event) => {
    const category = props.categories.find(c => c.name === event.category)
    const color = category ? category.color : 'gray'
    
    if (isTailwindColor(color)) {
        return {}
    }
    return {
        backgroundColor: color
    }
}

const getEventClasses = (event) => {
    const category = props.categories.find(c => c.name === event.category)
    const color = category ? category.color : 'gray'
    const baseClasses = 'text-white text-xs font-medium'
    
    if (isTailwindColor(color)) {
        return `${baseClasses} bg-${color}-500 hover:bg-${color}-600`
    }
    return baseClasses
}

const formatHour = (hour) => {
    return hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`
}

const formatFullDate = (date) => {
    return date.toLocaleDateString(locale.value, { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    })
}

const selectEvent = (event) => {
    emit('event-select', event)
}

const addEventAtTime = (date, hour) => {
    if (isPastDate(date)) return;
    const time = new Date(date)
    time.setHours(hour, 0, 0, 0)
    emit('add-event', { date, time })
}

const seeMoreEvents = (date, hour) => {
    const events = getEventsForDateAndTime(date, hour)
    emit('see-more', { date, events, hour })
}
</script>
